import { AttributionRole } from '../../../@types/CoproUserAttribution';

const ROLE_DIRIGEANT = 'dirigeant';

export type DashboardLayoutMode = 'full' | 'focused';

export interface DashboardCapabilities {
  layoutMode: DashboardLayoutMode;
  primaryRole: string;
  canSeeAnalysis: boolean;
  canSeeCRM: boolean;
  canSeePlanning: boolean;
  canEditPlanning: boolean;
  canSeePrealables: boolean;
  visibleSectionIds: string[] | undefined;
  expandSectionIds: string[];
  showNextActions: boolean;
}

const ROLE_PRIORITY: string[] = [
  ROLE_DIRIGEANT,
  AttributionRole.REFERENT,
  AttributionRole.ASSISTANT_REFERENT,
  AttributionRole.TRANSACTIONNAIRE,
  AttributionRole.GESTIONNAIRE_LOCATIF,
  AttributionRole.GESTIONNAIRE,
  AttributionRole.ASSISTANT_GESTIONNAIRE,
  AttributionRole.INTERVENANT,
];

const FULL_LAYOUT_ROLES = new Set<string>([
  ROLE_DIRIGEANT,
  AttributionRole.REFERENT,
  AttributionRole.ASSISTANT_REFERENT,
]);

const ROLE_TO_SECTION: Record<string, string> = {
  [AttributionRole.REFERENT]: 'referent',
  [AttributionRole.ASSISTANT_REFERENT]: 'referent',
  [AttributionRole.TRANSACTIONNAIRE]: 'transaction',
  [AttributionRole.GESTIONNAIRE]: 'gestionnaires',
  [AttributionRole.ASSISTANT_GESTIONNAIRE]: 'gestionnaires',
  [AttributionRole.GESTIONNAIRE_LOCATIF]: 'gestion-locative',
  [AttributionRole.INTERVENANT]: 'agences-partenaires',
};

function resolvePrimaryRole(roles: string[]): string {
  for (const priority of ROLE_PRIORITY) {
    if (roles.includes(priority)) {
      return priority;
    }
  }
  return roles[0] ?? '';
}

function resolveRoleSectionIds(roles: string[], isResponsibleForReferentModule: boolean): string[] {
  const sections: string[] = [];
  if (roles.includes(AttributionRole.REFERENT) || roles.includes(AttributionRole.ASSISTANT_REFERENT) || isResponsibleForReferentModule) {
    sections.push('referent');
  }
  if (roles.includes(AttributionRole.GESTIONNAIRE) || roles.includes(AttributionRole.ASSISTANT_GESTIONNAIRE)) {
    sections.push('gestionnaires');
  }
  if (roles.includes(AttributionRole.TRANSACTIONNAIRE)) {
    sections.push('transaction');
  }
  if (roles.includes(AttributionRole.GESTIONNAIRE_LOCATIF)) {
    sections.push('gestion-locative');
  }
  if (roles.includes(AttributionRole.INTERVENANT)) {
    sections.push('agences-partenaires');
  }
  return sections;
}

export function resolveCapabilities(
  roles: string[],
  isAdmin: boolean,
  isResponsibleForReferentModule: boolean,
): DashboardCapabilities {
  const primaryRole = isAdmin ? 'admin' : resolvePrimaryRole(roles);
  const roleSectionIds = resolveRoleSectionIds(roles, isResponsibleForReferentModule);

  const hasFullReferentAccess = isAdmin
    || roles.includes(ROLE_DIRIGEANT)
    || roles.includes(AttributionRole.REFERENT)
    || roles.includes(AttributionRole.ASSISTANT_REFERENT);

  const isFullLayout = isAdmin || FULL_LAYOUT_ROLES.has(primaryRole);
  const layoutMode: DashboardLayoutMode = isFullLayout ? 'full' : 'focused';

  return {
    layoutMode,
    primaryRole,
    canSeeAnalysis: isAdmin || roles.includes(ROLE_DIRIGEANT),
    canSeeCRM: isAdmin || roles.includes(ROLE_DIRIGEANT),
    canSeePlanning: true,
    canEditPlanning: hasFullReferentAccess,
    canSeePrealables: hasFullReferentAccess,
    visibleSectionIds: isFullLayout ? undefined : roleSectionIds,
    expandSectionIds: roleSectionIds,
    showNextActions: true,
  };
}

export function getRoleSectionLabel(primaryRole: string): string {
  switch (primaryRole) {
    case AttributionRole.TRANSACTIONNAIRE:
      return 'SERVICE TRANSACTION';
    case AttributionRole.GESTIONNAIRE_LOCATIF:
      return 'SERVICE GESTION LOCATIVE';
    case AttributionRole.INTERVENANT:
      return 'AGENCES PARTENAIRES';
    case AttributionRole.GESTIONNAIRE:
    case AttributionRole.ASSISTANT_GESTIONNAIRE:
      return 'GESTIONNAIRES';
    default:
      return '';
  }
}

export { ROLE_DIRIGEANT, ROLE_TO_SECTION };
